// src/components/fix-actions/redesign/action-detail-panel.tsx
//
// Detail panel for a single FixAction. Reused by both the split layout
// (right pane) and the accordion layout (expanded body). Binds to the real
// FixAction shape; the Generate button calls the real onGenerateAsset handler.

import { useState } from "react";
import type { FixAction, FixActionArtifact } from "../../../services/fix-actions/fix-actions-service";
import { sourceBadge, typeColor, Ic, SPARKLE } from "./shared";
import type { ExplainAnchor } from "./explain-popover";

export interface DetailCallbacks {
  onGenerateAsset: (
    actionId: number,
    actionTitle: string,
    actionTheme: string,
    artifactTitle: string,
    artifactType: string,
    artifactDescription: string,
    artifactIndex: number,
    totalArtifacts?: number
  ) => void;
  onExplainType: (anchor: ExplainAnchor) => void;
  onExplainWithAI: (topic: string) => void;
}

function priorityVisual(priority: string) {
  const p = String(priority || "").toUpperCase();
  if (p === "HIGH")
    return { color: "var(--infrared-coral)", bg: "var(--infrared-coral-10)", border: "var(--infrared-coral-20)", label: "High priority" };
  if (p === "MEDIUM")
    return { color: "var(--signal-blue)", bg: "var(--signal-blue-10)", border: "var(--signal-blue-20)", label: "Medium priority" };
  return { color: "var(--muted-foreground)", bg: "var(--muted)", border: "var(--border)", label: "Low priority" };
}

function effortColor(e: string) {
  return e === "Low" ? "var(--signal-green)" : e === "High" ? "var(--infrared-coral)" : "var(--signal-blue)";
}
function effortDots(e: string) {
  return e === "Low" ? 1 : e === "High" ? 3 : 2;
}
function impactColor(i: string) {
  return i === "High" ? "var(--alpha-teal)" : i === "Medium" ? "var(--signal-blue)" : "var(--muted-foreground)";
}

export function ActionDetailPanel({
  action,
  ready,
  callbacks,
}: {
  action: FixAction;
  ready: boolean;
  callbacks: DetailCallbacks;
}) {
  const [openArtifacts, setOpenArtifacts] = useState<Record<string, boolean>>({});
  const sb = sourceBadge(action.sourceModule);
  const pri = priorityVisual(action.priority);
  const artifacts: FixActionArtifact[] = action.artifacts || [];
  const score = Number(action.impactScore ?? 0);

  const explainFrom = (type: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    callbacks.onExplainType({ type, x: r.left, y: r.bottom + 8 });
  };

  const subBlock = (label: string, color: string, text?: string) =>
    text ? (
      <div style={{ paddingTop: 16, marginTop: 16, borderTop: "1px solid var(--border)" }}>
        <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color, marginBottom: 6 }}>{label}</div>
        <p style={{ fontSize: 13.5, color: "var(--muted-foreground)", lineHeight: 1.6, margin: 0 }}>{text}</p>
      </div>
    ) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* header band */}
      <div style={{ borderBottom: "1px solid var(--border)", padding: "16px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 9 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", background: sb.bg, color: sb.color, border: `1px solid ${sb.border}`, borderRadius: 6, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
            <Ic paths={sb.icon} size={11} sw={1.7} /> {sb.label}
          </span>
          {action.strategicTheme && (
            <span style={{ padding: "3px 8px", fontSize: 11, fontWeight: 600, background: "var(--muted)", color: "var(--muted-foreground)", border: "1px solid var(--border)", borderRadius: 6 }}>
              {action.strategicTheme}
            </span>
          )}
          <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", fontSize: 11, fontWeight: 600, borderRadius: 9999, background: pri.bg, color: pri.color, border: `1px solid ${pri.border}` }}>
            <span style={{ width: 6, height: 6, borderRadius: 9999, background: pri.color }} />
            {pri.label}
          </span>
        </div>
        <h3 style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-.02em", lineHeight: 1.25, margin: "0 0 5px", color: "var(--foreground)" }}>{action.title}</h3>
        <p style={{ fontSize: 13.5, color: "var(--muted-foreground)", lineHeight: 1.55, margin: 0 }}>{action.description}</p>

        {/* metric strip */}
        <div style={{ display: "flex", alignItems: "stretch", padding: "12px 0", border: "1px solid var(--border)", borderRadius: 12, background: "var(--card)", marginTop: 12 }}>
          <div style={{ flex: 1, minWidth: 0, padding: "0 14px" }}>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--muted-foreground)", marginBottom: 6 }}>Impact</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 6 }}>
              <span style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: impactColor(action.impactLevel), lineHeight: 1 }}>{score}</span>
              <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>/100 · {action.impactLevel}</span>
            </div>
            <div style={{ height: 6, borderRadius: 9999, background: "var(--muted)", overflow: "hidden" }}>
              <div style={{ height: "100%", borderRadius: 9999, width: `${ready ? score : 0}%`, background: "linear-gradient(90deg,var(--alpha-teal),var(--spectral-violet))", transition: "width .7s var(--ease-standard)" }} />
            </div>
          </div>
          <span style={{ width: 1, alignSelf: "stretch", background: "var(--border)", flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0, padding: "0 14px" }}>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--muted-foreground)", marginBottom: 6 }}>Effort</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
              <span style={{ display: "inline-flex", gap: 4 }}>
                {[0, 1, 2].map((d) => (
                  <span key={d} style={{ width: 7, height: 7, borderRadius: 9999, background: d < effortDots(action.effort) ? "var(--foreground)" : "var(--silver-mist)" }} />
                ))}
              </span>
              <span style={{ fontSize: 14, fontWeight: 600, color: effortColor(action.effort) }}>{action.effort}</span>
            </div>
          </div>
          <span style={{ width: 1, alignSelf: "stretch", background: "var(--border)", flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0, padding: "0 14px" }}>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--muted-foreground)", marginBottom: 6 }}>Assets</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 5, marginTop: 2 }}>
              <span style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{artifacts.length}</span>
              <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>to deploy</span>
            </div>
          </div>
        </div>
      </div>

      {/* Signal Analyst rationale */}
      {(action.observation || action.strategicGoal) && (
        <div style={{ padding: "16px 18px" }}>
          <div style={{ background: "var(--muted)", border: "1px solid var(--border)", borderRadius: 14, padding: "18px 20px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <span className="ai-orb" style={{ width: 30, height: 30, borderRadius: 9, background: "linear-gradient(135deg,var(--spectral-violet) 0%,var(--spectral-violet) 62%,var(--signal-blue) 155%)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", flexShrink: 0 }}>
                <Ic paths={SPARKLE} size={15} sw={1.7} />
              </span>
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--foreground)" }}>Signal Analyst</div>
                <div style={{ fontSize: 11, color: "var(--muted-foreground)" }}>Why this is on your roadmap</div>
              </div>
            </div>
            {action.observation && (
              <div style={{ display: "flex", gap: 14 }}>
                <span style={{ width: 3, borderRadius: 2, background: "linear-gradient(135deg,var(--spectral-violet),var(--signal-blue))", flexShrink: 0 }} />
                <p style={{ fontSize: 17, fontWeight: 500, lineHeight: 1.5, letterSpacing: "-.01em", color: "var(--foreground)", margin: 0 }}>{action.observation}</p>
              </div>
            )}
            {subBlock("Strategic goal", "var(--alpha-teal)", action.strategicGoal)}
            {subBlock("Why this influences AI reasoning", "var(--spectral-violet)", action.reasoningInfluence)}
            {subBlock("Expected outcome", "var(--signal-green)", action.expectedOutcome)}
          </div>
        </div>
      )}

      {/* assets */}
      <div style={{ paddingLeft: 18, paddingRight: 18, marginTop: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--foreground)" }}>Build these assets</div>
        <div style={{ fontSize: 12, color: "var(--muted-foreground)", marginTop: 2 }}>Generate each in the Optimization Studio. Click a type to learn what it does.</div>
      </div>
      <div style={{ paddingLeft: 18, paddingRight: 18, marginTop: 18 }}>
        <h4 style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--muted-foreground)", margin: "0 0 10px" }}>
          Optimization Assets ({artifacts.length})
        </h4>
        <div style={{ border: "1px solid var(--border)", borderRadius: 10, overflow: "hidden" }}>
          {artifacts.map((art, idx) => {
            const expanded = !!openArtifacts[art.id];
            return (
              <div key={art.id} style={{ borderBottom: idx < artifacts.length - 1 ? "1px solid var(--border)" : "none" }}>
                <div
                  style={{ padding: "12px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}
                  onClick={() => setOpenArtifacts((s) => ({ ...s, [art.id]: !s[art.id] }))}
                >
                  <span style={{ display: "inline-flex", color: "var(--muted-foreground)", transition: "transform .2s", transform: expanded ? "rotate(90deg)" : "rotate(0deg)", flexShrink: 0 }}>
                    <Ic paths={["m9 18 6-6-6-6"]} size={14} sw={2} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.3, color: "var(--foreground)" }}>{art.title}</div>
                    <button
                      onClick={(e) => explainFrom(art.type, e)}
                      style={{ fontSize: 11.5, color: typeColor(art.type), fontWeight: 500, background: "none", border: "none", padding: 0, marginTop: 2, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: "2px", textDecorationStyle: "dotted" }}
                    >
                      {art.type}
                    </button>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      callbacks.onGenerateAsset(action.id, action.title, action.strategicTheme || "", art.title, art.type, art.description || "", idx, artifacts.length);
                    }}
                    style={{ flexShrink: 0, padding: "5px 12px", background: "var(--alpha-teal)", color: "var(--accent-foreground)", fontSize: 11.5, fontWeight: 600, borderRadius: 7, border: "none", cursor: "pointer" }}
                  >
                    Generate
                  </button>
                </div>
                {expanded && (
                  <div style={{ padding: "0 14px 14px 38px", display: "flex", flexDirection: "column", gap: 12 }}>
                    <p style={{ fontSize: 13, color: "var(--muted-foreground)", lineHeight: 1.55, margin: 0 }}>{art.description}</p>
                    {art.expectedImpact && (
                      <div style={{ display: "flex", gap: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--alpha-teal)", flexShrink: 0, marginTop: 1 }}>Impact</span>
                        <p style={{ fontSize: 13, color: "var(--foreground)", lineHeight: 1.5, margin: 0 }}>{art.expectedImpact}</p>
                      </div>
                    )}
                    {art.deployment && art.deployment.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--muted-foreground)", marginBottom: 6 }}>Deployment</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                          {art.deployment.map((d, i) => (
                            <span key={i} style={{ fontSize: 11, fontWeight: 500, color: "var(--muted-foreground)", background: "var(--muted)", border: "1px solid var(--border)", borderRadius: 6, padding: "3px 9px" }}>{d}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Ask Upmode row */}
      <div style={{ paddingLeft: 18, paddingRight: 18 }}>
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={() => callbacks.onExplainWithAI(`About the action: ${action.title}`)}
            style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 600, color: "var(--spectral-violet)", background: "none", border: "none", padding: 0, cursor: "pointer" }}
          >
            <span style={{ width: 15, height: 15, borderRadius: 9999, background: "linear-gradient(135deg,var(--spectral-violet),var(--signal-blue))", flexShrink: 0 }} />
            Ask Upmode
          </button>
          {["Which asset has the fastest payoff?", "Draft a 90-day plan for this action"].map((q) => (
            <button
              key={q}
              className="ai-chip"
              onClick={() => callbacks.onExplainWithAI(q)}
              style={{ display: "inline-flex", alignItems: "center", height: 30, padding: "0 13px", borderRadius: 9999, background: "var(--card)", border: "1px solid var(--border)", fontSize: 12, fontWeight: 500, color: "var(--foreground)", cursor: "pointer", fontFamily: "inherit" }}
            >
              {q}
            </button>
          ))}
        </div>
      </div>

      <div style={{ height: 8 }} />
    </div>
  );
}
